import React, { useState, useEffect } from "react";
import { Spinner } from "../../Common";
import { getPlans, getPlansDetails } from "../../api";
import styles from "./plan.module.css";
import DataTable from "./../../Common/DataTable/DataTable";
import Button from "../../Common/Button";
import Offcanvas from "react-bootstrap/Offcanvas";
import singleBackArrow from "../../public/images/singleBackArrow.svg";
import PlanDetailsTable from "./PlanDetailsTable";
import { InputBox } from "../../Common";
import { planColumns } from "../../utils/tableHeader";
import CreatePlan from "./CreatePlan";
import PlanConfig from "./PlanConfig";
import withFeatureActions from "../../withFeatureActions";

function InvestingPlan({ featureAccess }) {
  const { readAccess, writeAccess, modifyAccess } = featureAccess;
  const [plans, setPlans] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [canvasData, setCanvasData] = useState();
  const [showDetailsCanvas, setShowDetailsCanvas] = useState(false);
  const [stepToShow, setStepToShow] = useState(1);
  const [formData, setFormData] = useState({});
  const [mode, setMode] = useState("create");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(20);
  const [searchText, setSearchText] = useState("");
  const [selectedPlanCode, setSelectedPlanCode] = useState("");
  const [showPlanConfig, setShowPlanConfig] = useState(false);

  const fetchPlanData = async (pageNo = currentPage, filter = searchText) => {
    setDataLoading(true);
    try {
      const response = await getPlans(pageNo, pageSize, filter);

      if (response.status === 200 && response.data) {
        setPlans(response?.data?.data?.plans || []);
        const totalCount = response.data.data.totalCount || 0;

        setTotalPages(Math.ceil(totalCount / pageSize));
      } else {
        setErrorMessage("Failed to fetch Plan data.");
      }
    } catch (error) {
      setErrorMessage("Error fetching data.");
    }
    setDataLoading(false);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleViewDetails = async (planCode) => {
    setDataLoading(true);
    setErrorMessage("");
    try {
      const response = await getPlansDetails(planCode);
      if (
        response.status === 200 &&
        response.data &&
        response.data.code === 200
      ) {
        if (response.data.data) {
          setCanvasData(response.data.data[0]);
        } else {
          setErrorMessage("No plan details found.");
          return;
        }
        setShowDetailsCanvas(true);
      } else {
        setErrorMessage("Failed to fetch Plan details.");
      }
    } catch (error) {
      setErrorMessage("Error fetching Plan details.");
    }
    setDataLoading(false);
  };

  const handleUpdatePlan = async (planCode) => {
    setDataLoading(true);
    try {
      const response = await getPlansDetails(planCode);
      if (response.status === 200 && response.data?.code === 200) {
        const fetchedData = response?.data?.data?.[0];
        const transformedData = {
          code: fetchedData.code || "",
          name: fetchedData.name || "",
          subscriptionAmt: fetchedData.subscriptionAmt || "",
          acctOpenFeesAmt: fetchedData.acctOpenFeesAmt || "",
          tradeCommissionAmt: fetchedData.tradeCommissionAmt || "",
          orderQtyLimit: fetchedData.orderQtyLimit ?? 0,
          shareCommissionAmt: fetchedData.shareCommissionAmt || "",
          aumCommissionPct: fetchedData.aumCommissionPct || "",
          minCommissionAmt: fetchedData.minCommissionAmt || "",
          currencyCd: fetchedData.currencyCd || "INR",
          planSeq: fetchedData.planSeq || "",
          subscriptionFlg: fetchedData.subscriptionFlg || false,
          planOverrideFlg: fetchedData.planOverrideFlg || false,
          defaultPlanFlg: fetchedData.defaultPlanFlg || false,
          feeDesc: fetchedData.feeDesc || "",
          freeTrades: fetchedData.freeTrades ?? 0,
          freeTradesQty: fetchedData.freeTradesQty ?? 0,
          freeTradesFreqCd: fetchedData.freeTradesFreqCd || "",
          strikePriceFlg: fetchedData.strikePriceFlg || false,
          stockScope: fetchedData.stockScope || "",
          hotlineSupportPhone: fetchedData.hotlineSupportPhone || "",
          hotlineSupportText: fetchedData.hotlineSupportText || "",
          planTitleAdjacent: fetchedData.planTitleAdjacent || "",
          planTitleAmount: fetchedData.planTitleAmount || "",
          status: fetchedData.status || "ACTIVE",
        };
        setFormData(transformedData);
        setShowDetailsCanvas(false);
        setStepToShow(2);
      } else {
        setErrorMessage("Failed to fetch Plan details.");
      }
    } catch (error) {
      setErrorMessage("Error fetching Plan details.");
    }
    setDataLoading(false);
  };

  const handleDuplicatePlan = async (planCode) => {
    setDataLoading(true);
    try {
      const response = await getPlansDetails(planCode);
      if (response.status === 200 && response.data?.code === 200) {
        const fetchedData = response?.data?.data?.[0];
        const transformedData = {
          // Clear these fields for new plan
          code: "",
          name: "",
          // Copy all other fields
          subscriptionAmt: fetchedData.subscriptionAmt || "",
          acctOpenFeesAmt: fetchedData.acctOpenFeesAmt || "",
          tradeCommissionAmt: fetchedData.tradeCommissionAmt || "",
          orderQtyLimit: fetchedData.orderQtyLimit ?? 0,
          shareCommissionAmt: fetchedData.shareCommissionAmt || "",
          aumCommissionPct: fetchedData.aumCommissionPct || "",
          minCommissionAmt: fetchedData.minCommissionAmt || "",
          currencyCd: fetchedData.currencyCd || "INR",
          planSeq: fetchedData.planSeq || "",
          subscriptionFlg: fetchedData.subscriptionFlg || false,
          planOverrideFlg: fetchedData.planOverrideFlg || false,
          defaultPlanFlg: fetchedData.defaultPlanFlg || false,
          feeDesc: fetchedData.feeDesc || "",
          freeTrades: fetchedData.freeTrades ?? 0,
          freeTradesQty: fetchedData.freeTradesQty ?? 0,
          freeTradesFreqCd: fetchedData.freeTradesFreqCd || "",
          strikePriceFlg: fetchedData.strikePriceFlg || false,
          stockScope: fetchedData.stockScope || "",
          hotlineSupportPhone: fetchedData.hotlineSupportPhone || "",
          hotlineSupportText: fetchedData.hotlineSupportText || "",
          planTitleAdjacent: fetchedData.planTitleAdjacent || "",
          planTitleAmount: fetchedData.planTitleAmount || "",
          status: "ACTIVE", // Default to ACTIVE for new plan
        };
        setFormData(transformedData);
        setMode("create"); // Important: set to create mode
        setShowDetailsCanvas(false);
        setStepToShow(2);
      } else {
        setErrorMessage("Failed to fetch Plan details for duplication.");
      }
    } catch (error) {
      setErrorMessage("Error fetching Plan details for duplication.");
    }
    setDataLoading(false);
  };

  const handleShowCreatePlan = () => {
    setMode("create");
    setStepToShow(2);
  };

  const handleBackToTable = () => {
    setCanvasData(null);
    setStepToShow(1);
    setShowDetailsCanvas(false);
    setFormData(null);
    setSelectedPlanCode("");
    setShowPlanConfig(false);
  };

  const handleAddConfig = (planData) => {
    setSelectedPlanCode(planData.code);
    setShowPlanConfig(true);
    setStepToShow(3);
  };

  useEffect(() => {
    let timeoutId;

    const fetchData = () => {
      fetchPlanData(currentPage, searchText);
    };

    if (searchText) {
      timeoutId = setTimeout(fetchData, 500);
    } else {
      fetchData();
    }

    return () => clearTimeout(timeoutId);
  }, [currentPage, searchText]);

  const handleClickRow = async (data) => {
    if (data.code) {
      setCanvasData({});
      setShowDetailsCanvas(true);
      await handleViewDetails(data.code);
      setStepToShow(1);
    }
  };

  const handleClickUpdatePlan = async (data) => {
    if (data && data.code) {
      setMode("update");
      await handleUpdatePlan(data.code);
      setStepToShow(2);
    }
  };

  const handleNewPlanAdded = () => {
    fetchPlanData();
    handleBackToTable();
    setStepToShow(1);
  };

  return (
    <div className={styles.container} style={{ position: "relative" }}>
      {stepToShow === 1 && (
        <>
          <div className={styles.topControls}>
            <div className={styles.searchContainer}>
              <InputBox
                placeholder="Search by Plan name, Plan code..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {errorMessage && <div style={{ color: "red" }}>{errorMessage}</div>}
          {dataLoading ? (
            <Spinner />
          ) : (
            <>
              {Array.isArray(plans) && plans.length > 0 ? (
                <>
                  <DataTable
                    columns={planColumns(featureAccess)}
                    list={plans}
                    handleClickRow={handleClickRow}
                    handleViewDetails={handleViewDetails}
                    totalPages={totalPages}
                    currentPage={currentPage}
                    setCurrentPage={handlePageChange}
                    pagination={true}
                    title="Plans List"
                    titleButton={writeAccess && <Button onClick={handleShowCreatePlan} title={"Create Plan"} />}
                    handleUpdatePlan={handleClickUpdatePlan}
                    handleDuplicatePlan={handleDuplicatePlan}
                    handleAddConfig={handleAddConfig}
                  />
                </>
              ) : (
                <p
                  style={{
                    color: "white",
                    marginTop: "80px",
                    textAlign: "center",
                  }}
                >
                  No Data Found
                </p>
              )}
            </>
          )}
        </>
      )}

      {stepToShow === 2 && (
        <div className={styles.createPlanContainers}>
          <div className={styles.backButtons}>
            <img
              alt="Back"
              src={singleBackArrow}
              onClick={handleBackToTable}
              className={styles.arrowButton}
            />
            <span
              style={{
                fontSize: "13px",
                marginLeft: "5px",
                cursor: "pointer",
                color: "white",
              }}
              onClick={handleBackToTable}
            >
              Back
            </span>
          </div>
          <CreatePlan
            mode={mode}
            initialData={formData}
            setStepToShow={setStepToShow}
            newPlanAdded={handleNewPlanAdded}
          />
        </div>
      )}

      <Offcanvas
        show={showDetailsCanvas}
        onHide={() => {
          setShowDetailsCanvas(false);
          setStepToShow(1);
        }}
        placement="end"
        className={styles.offcanvas}
        backdropClassName={styles.backgroundBlur}
        style={{ color: "white", backgroundColor: "#28282B", width: "650px" }}
      >
        <Offcanvas.Header
          closeButton
          style={{
            backgroundColor: "#343434",
            color: "white",
            fontSize: "20px",
            height: "50px",
          }}
        >
          <Offcanvas.Title
            style={{ color: "white", width: "100%", margin: "20px 20px" }}
          >
            Plan Details
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {dataLoading ? (
            <div
              style={{
                height: "100%",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Spinner animation="border" variant="light" />
            </div>
          ) : canvasData ? (
            <PlanDetailsTable planData={canvasData} />
          ) : (
            <p style={{ color: "white", textAlign: "center" }}>
              No Plan Details Available
            </p>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {stepToShow === 3 && showPlanConfig && (
        <div className={styles.createPlanContainers}>
          <div className={styles.backButtons}>
            <img
              alt="Back"
              src={singleBackArrow}
              onClick={handleBackToTable}
              className={styles.arrowButton}
            />
            <span
              style={{
                fontSize: "13px",
                marginLeft: "5px",
                cursor: "pointer",
                color: "white",
              }}
              onClick={handleBackToTable}
            >
              Back
            </span>
          </div>
          <PlanConfig planCode={selectedPlanCode} />
        </div>
      )}
    </div>
  );
}

export default withFeatureActions(InvestingPlan, "INVESTING", "Plans");
